import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, extractError, API_BASE } from "@/lib/api";
import { fmtFR } from "@/lib/dates";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, ChevronLeft, ChevronRight, Trash2, Calendar as CalIcon, MapPin, Euro, RefreshCw, Settings, Mail, Link2, Copy, Building2, ArrowRight, CheckCheck, Clock, Filter, X, FileText, AlertCircle, Check, Pencil, XCircle, Upload, RotateCcw } from "lucide-react";
import { formatIntermittentName } from "@/lib/intermittent";
import { MONTHS_FR } from "@/lib/time";

const PHASES = [
  { v: "contrat_a_faire", label: "Contrats à faire",  short: "Contrats à faire",  color: "bg-muted text-foreground border-border" },
  { v: "dpae_faite",       label: "DPAE faites",       short: "DPAE faites",       color: "bg-accent text-accent-foreground border-accent" },
  { v: "envoye",           label: "Contrats envoyés",  short: "Contrats envoyés",  color: "bg-primary/10 text-primary border-primary/40" },
  { v: "signe",            label: "Contrats signés",   short: "Contrats signés",   color: "bg-secondary text-secondary-foreground border-secondary" },
  { v: "paie_envoyee",     label: "Paie envoyées",     short: "Paie envoyées",     color: "bg-primary/20 text-primary border-primary/40" },
  { v: "salaire_paye",     label: "Salaires payés",    short: "Salaires payés",    color: "bg-emerald-500/15 text-emerald-200 border-emerald-500/50" },
  { v: "aem",              label: "AEM envoyés",       short: "AEM envoyés",       color: "bg-primary text-primary-foreground border-primary" },
  { v: "dsn",              label: "DSN faite",         short: "DSN faite",         color: "bg-secondary text-secondary-foreground border-secondary shadow-glow" },
  { v: "annule",           label: "Contrats annulés",  short: "Contrats annulés",  color: "bg-destructive/10 text-destructive border-destructive/40" },
];

const EMPTY_DECL = {
  intermittent_id: "", status: "contrat_a_faire",
  start_date: "", end_date: "", start_time: "",
  cost_ht_client: 0, margin_rate: 20, cost_employer: 0,
  event_id: "", event_name: "", event_location: "",
  client_id: "", role: "", role_type: "technicien", cachet_hours: 0, cachet_count: 1,
  period: "", notes: "",
  fees_included: true,
  contract_expenses: [],  // [{ id?, title, category, amount }]
};

// Catalog frais aligné sur EXPENSE_TYPES backend
const DECL_EXPENSE_CATS = [
  { v: "transport_train",    label: "Train (Déplacement)" },
  { v: "transport_avion",    label: "Avion (Déplacement)" },
  { v: "transport_taxi",     label: "Taxi (Déplacement)" },
  { v: "transport_parking",  label: "Parking (Déplacement)" },
  { v: "transport_peage",    label: "Péage (Déplacement)" },
  { v: "transport_voiture",  label: "Voiture perso (Déplacement)" },
  { v: "transport_location", label: "Voiture location (Déplacement)" },
  { v: "hebergement",        label: "Hébergement" },
  { v: "restauration",       label: "Restauration" },
  { v: "autre",              label: "Autre" },
];

function pad2(n) { return String(n).padStart(2, "0"); }

function IntermittentPill({ p }) {
  if (!p) return null;
  return (
    <div className="flex items-center gap-2">
      <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center text-[10px] font-semibold shrink-0">
        {p.last_name?.[0]}{p.first_name?.[0]}
      </div>
      <div className="text-sm font-medium truncate">{formatIntermittentName(p)}</div>
    </div>
  );
}

function DeclCard({ d, onOpen, onDragStart, selected, onToggleSelect, onToggleInvoice, onApproveChange, onRejectChange, onDeleteChange }) {
  const color = d.client?.color || null;
  const cachet = Number(d.cachet_hours) || 0;
  const cachetCount = Math.max(1, parseInt(d.cachet_count) || 1);
  const pending = d.pending_change;
  const last = d.last_change;
  const wasModified = last && last.status === "handled" && last.action === "modify";
  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(d.id, e)}
      data-testid={`decl-card-${d.id}`}
      className={`fade-up bg-card border rounded-xl p-3 shadow-warm hover:shadow-warm-lg hover:-translate-y-0.5 transition-transform active:scale-[0.98] space-y-2 relative ${selected ? "ring-2 ring-primary" : ""} ${pending ? "ring-2 ring-amber-400" : ""}`}
      style={color ? { borderLeftWidth: 4, borderLeftColor: color, borderTopColor: color + "33", borderRightColor: color + "33", borderBottomColor: color + "33" } : undefined}
    >
      <div className="absolute top-2 right-2 z-10" onClick={e => e.stopPropagation()}>
        <Checkbox checked={!!selected} onCheckedChange={() => onToggleSelect(d.id)} data-testid={`select-decl-${d.id}`} />
      </div>
      {pending && (
        <div
          className="rounded-lg border-2 border-amber-400 bg-amber-500/15 p-2 space-y-2"
          onClick={e => e.stopPropagation()}
          data-testid={`pending-change-${d.id}`}
        >
          <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-amber-200">
            <AlertCircle className="h-3.5 w-3.5" />
            Demande client&nbsp;: {pending.action === "cancel" ? "annulation" : "modification"}
          </div>
          {pending.message && (
            <div className="text-[11px] text-amber-100 leading-snug bg-amber-500/10 rounded px-2 py-1 border border-amber-400/40 whitespace-pre-line">
              {pending.message}
            </div>
          )}
          <div className="flex items-center gap-1 flex-wrap">
            <Button
              size="sm"
              variant="outline"
              className="rounded-full h-7 px-2 text-[10px] flex-1 min-w-0 border-emerald-500 text-emerald-300 hover:bg-emerald-500/15"
              onClick={() => onApproveChange(d, pending)}
              data-testid={`approve-change-${d.id}`}
            >
              <Check className="h-3 w-3 mr-1 shrink-0" /> Approuver
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="rounded-full h-7 px-2 text-[10px] flex-1 min-w-0 border-destructive/60 text-destructive hover:bg-destructive/5"
              onClick={() => onRejectChange(d, pending)}
              data-testid={`reject-change-${d.id}`}
            >
              <X className="h-3 w-3 mr-1 shrink-0" /> Refuser
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="rounded-full h-7 w-7 p-0 text-muted-foreground hover:text-destructive hover:bg-destructive/5 shrink-0"
              onClick={() => onDeleteChange(d, pending)}
              data-testid={`delete-change-${d.id}`}
              title="Supprimer cette demande (test/erreur)"
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
        </div>
      )}
      <div className="cursor-pointer space-y-2" onClick={() => onOpen(d)}>
        <div className="pr-8"><IntermittentPill p={d.intermittent} /></div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <Badge variant="outline" className="text-[10px] rounded-full px-1.5 py-0 h-4 border-border/60">
            {d.role_type === "artiste" ? "Artiste" : "Technicien"}
          </Badge>
          {d.role && <span className="text-[11px] text-foreground/80 font-medium truncate">{d.role}</span>}
          {wasModified && (
            <Badge
              className="text-[10px] rounded-full px-1.5 py-0 h-4 bg-blue-500/15 text-blue-200 border-blue-500/50 font-semibold cursor-help"
              title={`Modifié le ${last.resolved_at?.slice(0,10)}${last.message ? ` — ${last.message}` : ""}`}
              data-testid={`modified-badge-${d.id}`}
            >
              <Pencil className="h-2.5 w-2.5 mr-0.5" /> Modifié
            </Badge>
          )}
        </div>
        {d.client?.name && (
          <div className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: color || "var(--muted-foreground)" }}>
            {d.client.name}
          </div>
        )}
        {(d.event_name || d.event_id) && (
          <div className="text-xs font-medium text-foreground/80 truncate">
            {d.event_name || d.event_link_name || "—"}
          </div>
        )}
        <div className="flex items-center gap-3 text-[11px] text-muted-foreground">
          {d.start_date && (
            <div className="flex items-center gap-1"><CalIcon className="h-3 w-3" />{fmtFR(d.start_date)}{d.end_date && d.end_date !== d.start_date ? `→${fmtFR(d.end_date)}` : ""}{d.start_time ? ` · ${d.start_time}` : ""}</div>
          )}
        </div>
        {d.event_location && (
          <div className="flex items-center gap-1 text-[11px] text-muted-foreground">
            <MapPin className="h-3 w-3" /> {d.event_location}
          </div>
        )}
        {cachet > 0 && (
          <div className="flex items-center gap-1 text-[11px] text-muted-foreground">
            <Clock className="h-3 w-3" /> <span className="font-semibold text-foreground tabular-nums">{cachetCount}</span>&nbsp;cachet{cachetCount > 1 ? "s" : ""} de&nbsp;<span className="font-semibold text-foreground tabular-nums">{cachet.toLocaleString("fr-FR")} h</span>
          </div>
        )}
        {Number(d.cost_employer) > 0 && (
          <div className="flex items-center justify-between pt-2 border-t border-border/50">
            <span className="text-[11px] text-muted-foreground">Coût employeur</span>
            <span className="text-xs font-semibold text-primary tabular-nums">{Number(d.cost_employer).toLocaleString("fr-FR")} €</span>
          </div>
        )}
        {(() => {
          const totalContract = (d.contract_expenses || []).reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
          const totalPortal = Number(d.expenses_total || 0);
          const totalFrais = totalContract + totalPortal;
          if (totalFrais <= 0) return null;
          const inclusLabel = d.fees_included ? "Inclus" : "En sus";
          return (
            <div className="flex items-center justify-between text-[11px]" data-testid={`decl-frais-${d.id}`}>
              <span className="text-muted-foreground">
                Frais
                {totalPortal > 0 && totalContract > 0 && (
                  <span className="text-[9px] text-muted-foreground/70"> · {totalContract.toFixed(0)}€ prévus + {totalPortal.toFixed(0)}€ saisis</span>
                )}
                {totalPortal > 0 && totalContract === 0 && (
                  <span className="text-[9px] text-muted-foreground/70"> · saisis</span>
                )}
                {totalPortal === 0 && totalContract > 0 && (
                  <span className="text-[9px] text-muted-foreground/70"> · prévus</span>
                )}
              </span>
              <span className="font-semibold text-foreground tabular-nums">
                {totalFrais.toLocaleString("fr-FR")} €
                <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider ${d.fees_included ? "bg-emerald-500/15 text-emerald-200" : "bg-secondary/40 text-primary"}`}>
                  {inclusLabel}
                </span>
              </span>
            </div>
          );
        })()}
      </div>
      {d.status !== "annule" && (
      <div className="pt-2 border-t border-border/50 space-y-1" onClick={e => e.stopPropagation()}>
        <label
          className={`flex items-center gap-2 cursor-pointer select-none rounded-md px-1 -mx-1 ${d.invoice_sent ? "text-primary" : "text-muted-foreground"}`}
          data-testid={`invoice-toggle-${d.id}`}
        >
          <Checkbox
            checked={!!d.invoice_sent}
            onCheckedChange={() => onToggleInvoice(d, "sent")}
            data-testid={`invoice-checkbox-${d.id}`}
          />
          <span className="text-[11px] font-semibold flex items-center gap-1">
            <FileText className="h-3 w-3" /> Facture envoyée
          </span>
        </label>
        <label
          className={`flex items-center gap-2 cursor-pointer select-none rounded-md px-1 -mx-1 ${d.invoice_paid ? "text-emerald-300" : "text-muted-foreground"} ${!d.invoice_sent ? "opacity-50" : ""}`}
          data-testid={`invoice-paid-toggle-${d.id}`}
          title={!d.invoice_sent ? "Cochez d'abord « Facture envoyée »" : ""}
        >
          <Checkbox
            checked={!!d.invoice_paid}
            onCheckedChange={() => onToggleInvoice(d, "paid")}
            data-testid={`invoice-paid-checkbox-${d.id}`}
          />
          <span className="text-[11px] font-semibold flex items-center gap-1">
            <Euro className="h-3 w-3" /> Facture payée
          </span>
        </label>
      </div>
      )}
    </div>
  );
}

export default function DeclarationsPage() {
  const now = new Date();
  const [period, setPeriod] = useState(`${now.getFullYear()}-${pad2(now.getMonth() + 1)}`);
  const [intermittents, setIntermittents] = useState([]);
  const [declarations, setDeclarations] = useState([]);
  const [events, setEvents] = useState([]);
  const [summary, setSummary] = useState(null);

  const [declDialogOpen, setDeclDialogOpen] = useState(false);
  const [declForm, setDeclForm] = useState({ ...EMPTY_DECL, period });
  const [declEditingId, setDeclEditingId] = useState(null);
  const [portalExpenses, setPortalExpenses] = useState([]);  // Frais uploadés via portail intermittent (read-only pour admin)
  const [intermittentQuery, setIntermittentQuery] = useState("");

  const [gmailOpen, setGmailOpen] = useState(false);
  const [gmailConfig, setGmailConfig] = useState(null);
  const [gmailForm, setGmailForm] = useState({ email: "", app_password: "", enabled: true });
  const [syncing, setSyncing] = useState(false);

  const [clientLinksOpen, setClientLinksOpen] = useState(false);
  const [publicLinks, setPublicLinks] = useState([]);
  const [clients, setClients] = useState([]);
  const [orphansOpen, setOrphansOpen] = useState(false);
  const [orphans, setOrphans] = useState({ orphans: [], count: 0, total_amount: 0, total_km: 0 });
  const [orphansLoading, setOrphansLoading] = useState(false);
  const [purging, setPurging] = useState(false);
  const [deletionsOpen, setDeletionsOpen] = useState(false);
  const [deletions, setDeletions] = useState([]);
  const [deletionsLoading, setDeletionsLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState({}); // { [id]: true }
  const [filters, setFilters] = useState({ intermittent_id: "", client_id: "", event_id: "", invoice: "", date_from: "", date_to: "", role: "" });
  const [filterIntermittentQuery, setFilterIntermittentQuery] = useState("");
  const [rejectDialog, setRejectDialog] = useState({ open: false, decl: null, change: null, reason: "" });
  const [rejecting, setRejecting] = useState(false);

  const approveChange = async (d, change) => {
    const actionLbl = change.action === "cancel" ? "annulation" : "modification";
    if (!window.confirm(`Approuver la demande d'${actionLbl} du client${change.action === "cancel" ? " ? Le contrat sera déplacé dans « Contrats annulés »." : " ?"}`)) return;
    try {
      await api.patch(`/change-requests/${change.id || change._id || d.pending_change?.id}`, { status: "handled" });
      toast.success(`Demande d'${actionLbl} approuvée`);
      await load();
    } catch (e) { toast.error(extractError(e)); }
  };

  const openRejectDialog = (d, change) => {
    setRejectDialog({ open: true, decl: d, change, reason: "" });
  };

  const deleteChange = async (d, change) => {
    const crId = change?.id || change?._id || d.pending_change?.id;
    if (!crId) { toast.error("Demande introuvable"); return; }
    if (!window.confirm("Supprimer définitivement cette demande client ? Utile pour effacer un test ou une erreur — la déclaration liée n'est pas modifiée.")) return;
    try {
      await api.delete(`/change-requests/${crId}`);
      toast.success("Demande supprimée");
      await load();
    } catch (e) { toast.error(extractError(e)); }
  };

  const submitReject = async () => {
    const reason = rejectDialog.reason.trim();
    if (!reason) { toast.error("Motif obligatoire"); return; }
    setRejecting(true);
    try {
      await api.patch(`/change-requests/${rejectDialog.change.id}`, {
        status: "rejected",
        rejection_reason: reason,
      });
      toast.success("Demande refusée");
      setRejectDialog({ open: false, decl: null, change: null, reason: "" });
      await load();
    } catch (e) { toast.error(extractError(e)); }
    finally { setRejecting(false); }
  };

  const loadGmail = async () => {
    try {
      const { data } = await api.get("/integrations/gmail");
      setGmailConfig(data);
      if (data.configured) setGmailForm({ email: data.email, app_password: "", enabled: data.enabled });
    } catch (e) { /* silent */ }
  };
  useEffect(() => { loadGmail(); }, []);

  const saveGmail = async () => {
    try {
      if (!gmailForm.email || !gmailForm.app_password) {
        toast.error("Email et mot de passe d'application requis"); return;
      }
      await api.post("/integrations/gmail", gmailForm);
      toast.success("Configuration Gmail enregistrée");
      setGmailOpen(false);
      loadGmail();
    } catch (e) { toast.error(extractError(e)); }
  };

  const removeGmail = async () => {
    if (!confirm("Supprimer la configuration Gmail ?")) return;
    try {
      await api.delete("/integrations/gmail");
      toast.success("Configuration supprimée");
      setGmailConfig({ configured: false });
      setGmailForm({ email: "", app_password: "", enabled: true });
    } catch (e) { toast.error(extractError(e)); }
  };

  const syncNow = async () => {
    if (!gmailConfig?.configured) {
      setGmailOpen(true);
      toast.info("Configurez d'abord votre boîte Gmail");
      return;
    }
    setSyncing(true);
    try {
      const { data } = await api.post("/integrations/gmail/sync");
      if (data.skipped) {
        toast.error(data.reason || "Synchronisation ignorée");
      } else if (data.errors?.length) {
        toast.error(data.errors[0]);
      } else {
        const msg = `${data.scanned || 0} email(s) analysé(s) · ${data.moved || 0} carte(s) déplacée(s)${data.unmatched_names?.length ? ` · ${data.unmatched_names.length} nom(s) sans match` : ""}`;
        toast.success(msg);
      }
      loadGmail();
      load();
    } catch (e) { toast.error(extractError(e)); }
    finally { setSyncing(false); }
  };

  const loadPublicLinks = async () => {
    try {
      const [{ data: l }, { data: cs }] = await Promise.all([
        api.get("/public-links"),
        api.get("/clients").catch(() => ({ data: [] })),
      ]);
      setPublicLinks(l);
      setClients(cs);
    } catch (e) { toast.error(extractError(e)); }
  };
  const openClientLinks = () => { loadPublicLinks(); setClientLinksOpen(true); };
  const createOrGetClientLink = async (client) => {
    try {
      const existing = publicLinks.find(l => l.type === "declaration_request" && l.client_id === client.id);
      if (existing) { await navigator.clipboard.writeText(existing.url).catch(() => {}); toast.success("Lien copié"); return; }
      const slug = window.prompt(
        "URL personnalisée du formulaire (optionnel) — laissez vide pour un token aléatoire.\n\nEx : demande-seal-prod → …/demande-declaration/demande-seal-prod",
        ""
      );
      if (slug === null) return;
      const { data } = await api.post("/public-links", {
        label: `Demande — ${client.name}`,
        client_id: client.id,
        active: true,
        type: "declaration_request",
        slug: (slug || "").trim() || null,
      });
      await navigator.clipboard.writeText(data.url).catch(() => {});
      toast.success("Lien créé et copié");
      loadPublicLinks();
    } catch (e) { toast.error(extractError(e)); }
  };
  const editLinkSlug = async (l) => {
    const raw = window.prompt(
      "URL personnalisée (a-z, 0-9, tirets — 3 à 60 caractères). Laissez vide pour un token aléatoire.",
      l.slug || ""
    );
    if (raw === null) return;
    try {
      await api.patch(`/public-links/${l.token}`, { slug: raw.trim() });
      toast.success(raw.trim() ? "URL personnalisée mise à jour" : "URL personnalisée retirée");
      loadPublicLinks();
    } catch (e) { toast.error(extractError(e)); }
  };
  const toggleLinkActive = async (l) => {
    try {
      await api.patch(`/public-links/${l.token}`, { active: !l.active });
      loadPublicLinks();
    } catch (e) { toast.error(extractError(e)); }
  };
  const removePublicLink = async (token) => {
    if (!confirm("Supprimer ce lien ?")) return;
    try { await api.delete(`/public-links/${token}`); toast.success("Supprimé"); loadPublicLinks(); }
    catch (e) { toast.error(extractError(e)); }
  };
  const copyLink = async (url) => {
    try { await navigator.clipboard.writeText(url); toast.success("Lien copié"); }
    catch { toast.error("Copie impossible"); }
  };

  const load = async () => {
    try {
      const [{ data: pers }, { data: decls }, { data: sm }, { data: evs }, { data: cls }] = await Promise.all([
        api.get("/intermittents"),
        api.get("/declarations", { params: { period } }),
        api.get("/declarations/summary", { params: { period } }),
        api.get("/events").catch(() => ({ data: [] })),
        api.get("/clients").catch(() => ({ data: [] })),
      ]);
      setIntermittents(pers);
      setClients(cls);
      const evMap = Object.fromEntries((evs || []).map(e => [e.id, e]));
      decls.forEach(d => {
        if (d.event_id && evMap[d.event_id]) {
          d.event_link_name = evMap[d.event_id].name;
          if (!d.event_name) d.event_name = evMap[d.event_id].name;
          if (!d.event_location) d.event_location = evMap[d.event_id].location;
        }
      });
      setDeclarations(decls);
      setSummary(sm);
      setEvents(evs);
    } catch (e) { toast.error(extractError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [period]);

  const [y, m] = period.split("-").map(Number);
  const label = `${MONTHS_FR[m - 1]} ${y}`;
  const shiftMonth = (delta) => {
    const d = new Date(y, m - 1 + delta, 1);
    setPeriod(`${d.getFullYear()}-${pad2(d.getMonth() + 1)}`);
  };

  const grouped = useMemo(() => {
    const g = Object.fromEntries(PHASES.map(p => [p.v, []]));
    declarations.forEach(d => {
      if (filters.intermittent_id && d.intermittent_id !== filters.intermittent_id) return;
      if (filters.client_id && d.client_id !== filters.client_id) return;
      if (filters.event_id && d.event_id !== filters.event_id) return;
      if (filters.invoice === "sent" && !d.invoice_sent) return;
      if (filters.invoice === "pending" && d.invoice_sent) return;
      if (filters.role) {
        const q = filters.role.trim().toLowerCase();
        if (q && !(d.role || "").toLowerCase().includes(q)) return;
      }
      // Filtre plage de dates du contrat (chevauchement avec [date_from ; date_to]).
      if (filters.date_from || filters.date_to) {
        const ds = d.start_date || "";
        const de = d.end_date || d.start_date || "";
        if (!ds) return;
        if (filters.date_from && de && de < filters.date_from) return;
        if (filters.date_to && ds && ds > filters.date_to) return;
      }
      (g[d.status] || g.contrat_a_faire).push(d);
    });
    // Tri chronologique croissant (du 1er au dernier jour du mois), fallback nom intermittent
    Object.keys(g).forEach(k => {
      g[k].sort((a, b) => {
        const da = a.start_date || "9999-12-31";
        const db = b.start_date || "9999-12-31";
        if (da !== db) return da.localeCompare(db);
        const na = `${a.intermittent_last_name || ""} ${a.intermittent_first_name || ""}`.toLowerCase();
        const nb = `${b.intermittent_last_name || ""} ${b.intermittent_first_name || ""}`.toLowerCase();
        return na.localeCompare(nb, "fr");
      });
    });
    return g;
  }, [declarations, filters]);

  const filteredCount = useMemo(
    () => Object.values(grouped).reduce((s, arr) => s + arr.length, 0),
    [grouped]
  );
  const hasActiveFilter = !!(filters.intermittent_id || filters.client_id || filters.event_id || filters.invoice || filters.date_from || filters.date_to || filters.role);
  const unbilledCount = useMemo(
    () => declarations.filter(d => !d.invoice_sent && d.status !== "annule").length,
    [declarations]
  );
  const unbilledHt = useMemo(
    () => declarations.filter(d => !d.invoice_sent && d.status !== "annule").reduce((s, d) => s + (Number(d.cost_ht_client) || 0), 0),
    [declarations]
  );
  const cancelledCount = useMemo(
    () => declarations.filter(d => d.status === "annule").length,
    [declarations]
  );

  const roleSuggestions = useMemo(() => {
    const seed = [
      "Régisseur général", "Régisseur son", "Régisseur lumière", "Régisseur plateau",
      "Ingénieur son", "Assistant son", "Backliner",
      "Chef opérateur son", "Chef opérateur lumière", "Chef opérateur vidéo",
      "Technicien son", "Technicien lumière", "Technicien vidéo", "Technicien plateau",
      "Roadie", "Rigger", "Machiniste",
      "Danseur", "Danseuse", "Chorégraphe", "Assistant chorégraphique",
      "Chanteur", "Chanteuse", "Musicien", "DJ",
      "Comédien", "Comédienne", "Metteur en scène",
      "Photographe", "Cadreur", "Monteur", "Réalisateur",
    ];
    const set = new Set(seed.filter(Boolean));
    intermittents.forEach(p => { if (p.role) set.add(p.role); });
    declarations.forEach(d => { if (d.role) set.add(d.role); });
    return Array.from(set).sort((a, b) => a.localeCompare(b, "fr"));
  }, [intermittents, declarations]);

  const onDragStart = (id, e) => { e.dataTransfer.setData("text/did", id); };
  const onDrop = async (phase, e) => {
    e.preventDefault();
    const id = e.dataTransfer.getData("text/did");
    if (!id) return;
    try {
      await api.patch(`/declarations/${id}/status`, { status: phase });
      setDeclarations(list => list.map(x => x.id === id ? { ...x, status: phase } : x));
      const { data: sm } = await api.get("/declarations/summary", { params: { period } });
      setSummary(sm);
      toast.success(`Déplacé vers ${PHASES.find(p => p.v === phase)?.short}`);
    } catch (err) { toast.error(extractError(err)); }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = { ...prev };
      if (next[id]) delete next[id]; else next[id] = true;
      return next;
    });
  };
  const toggleInvoice = async (d, field = "sent") => {
    // field = "sent" | "paid" — applique la cascade sent/paid côté UI + backend
    let nextSent = !!d.invoice_sent;
    let nextPaid = !!d.invoice_paid;
    if (field === "sent") {
      nextSent = !d.invoice_sent;
      if (!nextSent) nextPaid = false; // décocher envoyée → décocher payée
    } else {
      nextPaid = !d.invoice_paid;
      if (nextPaid) nextSent = true;   // cocher payée → cocher envoyée
    }
    const prev = { invoice_sent: d.invoice_sent, invoice_paid: d.invoice_paid };
    setDeclarations(list => list.map(x => x.id === d.id ? { ...x, invoice_sent: nextSent, invoice_paid: nextPaid } : x));
    try {
      await api.patch(`/declarations/${d.id}/invoice`, { invoice_sent: nextSent, invoice_paid: nextPaid });
      toast.success(nextPaid ? "Facture marquée payée" : nextSent ? "Facture marquée envoyée" : "Facture repassée à facturer");
    } catch (err) {
      setDeclarations(list => list.map(x => x.id === d.id ? { ...x, ...prev } : x));
      toast.error(extractError(err));
    }
  };
  const bulkInvoiceInColumn = async (phase, field) => {
    // field = "sent" | "paid" — applique la cascade
    const ids = (grouped[phase] || []).filter(d => selectedIds[d.id]).map(d => d.id);
    if (ids.length === 0) { toast.error("Aucune carte sélectionnée dans cette colonne"); return; }
    const body = field === "paid"
      ? { invoice_sent: true, invoice_paid: true }
      : { invoice_sent: true };
    try {
      await api.patch(`/declarations/bulk-invoice`, { ids, ...body });
      setDeclarations(list => list.map(x => ids.includes(x.id) ? { ...x, ...body } : x));
      setSelectedIds(prev => { const n = { ...prev }; ids.forEach(id => delete n[id]); return n; });
      toast.success(`${ids.length} contrat(s) → ${field === "paid" ? "Facture payée" : "Facture envoyée"}`);
    } catch (err) { toast.error(extractError(err)); }
  };
  const clearSelection = () => setSelectedIds({});
  const selectAllInColumn = (phase) => {
    const ids = (grouped[phase] || []).map(d => d.id);
    const allSelected = ids.length > 0 && ids.every(id => selectedIds[id]);
    setSelectedIds(prev => {
      const next = { ...prev };
      if (allSelected) ids.forEach(id => { delete next[id]; });
      else ids.forEach(id => { next[id] = true; });
      return next;
    });
  };
  const moveSelectedTo = async (fromPhase, targetPhaseV) => {
    const targetPhase = PHASES.find(p => p.v === targetPhaseV);
    if (!targetPhase) return;
    if (targetPhase.v === fromPhase) { toast.error("Déjà dans cette colonne"); return; }
    const ids = (grouped[fromPhase] || []).filter(d => selectedIds[d.id]).map(d => d.id);
    if (ids.length === 0) { toast.error("Aucune carte sélectionnée dans cette colonne"); return; }
    try {
      await api.patch(`/declarations/bulk-status`, { ids, status: targetPhase.v });
      setDeclarations(list => list.map(x => ids.includes(x.id) ? { ...x, status: targetPhase.v } : x));
      const { data: sm } = await api.get("/declarations/summary", { params: { period } });
      setSummary(sm);
      setSelectedIds(prev => { const n = { ...prev }; ids.forEach(id => delete n[id]); return n; });
      toast.success(`${ids.length} carte(s) → ${targetPhase.short}`);
    } catch (err) { toast.error(extractError(err)); }
  };
  const moveSelectedToNext = async (fromPhase) => {
    const idx = PHASES.findIndex(p => p.v === fromPhase);
    const nextPhase = PHASES[idx + 1];
    if (!nextPhase) { toast.error("Dernière colonne"); return; }
    await moveSelectedTo(fromPhase, nextPhase.v);
  };

  const openNewDecl = (prefill = {}) => {
    setDeclForm({ ...EMPTY_DECL, period, ...prefill });
    setDeclEditingId(null);
    setIntermittentQuery("");
    setDeclDialogOpen(true);
  };

  // Pré-remplissage depuis query params (?prefill_event=… ?prefill_name=… ?prefill_role=… ?prefill_role_type=…&prefill_date=… ?prefill_event_name=…)
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (!searchParams.get("prefill_event") && !searchParams.get("prefill_name")) return;
    const startDate = searchParams.get("prefill_date") || "";
    const prefill = {
      event_id: searchParams.get("prefill_event") || "",
      event_name: searchParams.get("prefill_event_name") || "",
      role: searchParams.get("prefill_role") || "",
      role_type: searchParams.get("prefill_role_type") || "artiste",
      start_date: startDate,
      end_date: startDate,
      // Dérive la période (mois de rattachement) à partir de la date de début — sinon la
      // déclaration se retrouve dans le Kanban du mois courant au lieu du mois de l'événement.
      period: startDate ? startDate.slice(0, 7) : "",
      notes: searchParams.get("prefill_comment") || "",
    };
    openNewDecl(prefill);
    if (prefill.event_name || searchParams.get("prefill_name")) {
      toast.success(`Formulaire pré-rempli pour ${searchParams.get("prefill_name") || prefill.event_name}`);
    }
    // Nettoie l'URL pour éviter la réouverture au rechargement
    const clean = new URLSearchParams(searchParams);
    ["prefill_event", "prefill_name", "prefill_role", "prefill_role_type", "prefill_date", "prefill_event_name", "prefill_comment"].forEach(k => clean.delete(k));
    setSearchParams(clean, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const openOrphans = async () => {
    setOrphansOpen(true);
    setOrphansLoading(true);
    try {
      const { data } = await api.get("/admin/orphan-expenses");
      setOrphans(data);
    } catch (e) {
      toast.error(extractError(e));
      setOrphansOpen(false);
    } finally { setOrphansLoading(false); }
  };
  const purgeOrphans = async () => {
    if (!window.confirm(`Purger définitivement ${orphans.count} frais orphelin(s) ? Cette action est irréversible.`)) return;
    setPurging(true);
    try {
      const { data } = await api.post("/admin/orphan-expenses/purge", {});
      toast.success(`${data.deleted} frais orphelin(s) supprimé(s)`);
      setOrphans({ orphans: [], count: 0, total_amount: 0, total_km: 0 });
      setOrphansOpen(false);
    } catch (e) { toast.error(extractError(e)); }
    finally { setPurging(false); }
  };
  const openDeletions = async () => {
    setDeletionsOpen(true);
    setDeletionsLoading(true);
    try {
      const { data } = await api.get("/change-requests/deletions");
      setDeletions(data || []);
    } catch (e) { toast.error(extractError(e)); }
    finally { setDeletionsLoading(false); }
  };

  const restoreDeletion = async (entry) => {
    if (!window.confirm(`Restaurer cette demande d'${entry.cr_action === "cancel" ? "annulation" : "modification"} ? Elle réapparaîtra dans son état d'avant suppression.`)) return;
    try {
      await api.post(`/change-requests/deletions/${entry.id}/restore`);
      toast.success("Demande restaurée");
      // recharger le journal et le kanban
      const { data } = await api.get("/change-requests/deletions");
      setDeletions(data || []);
      await load();
    } catch (e) { toast.error(extractError(e)); }
  };
  const openEditDecl = (d) => {
    setDeclForm({
      intermittent_id: d.intermittent_id,
      status: d.status || "contrat_a_faire",
      start_date: d.start_date || "",
      end_date: d.end_date || "",
      start_time: d.start_time || "",
      cost_ht_client: d.cost_ht_client || 0,
      margin_rate: d.margin_rate || 0,
      cost_employer: d.cost_employer || 0,
      event_id: d.event_id || "",
      event_name: d.event_name || "",
      event_location: d.event_location || "",
      client_id: d.client_id || "",
      role: d.role || "",
      role_type: d.role_type || "technicien",
      cachet_hours: d.cachet_hours || 0,
      cachet_count: parseInt(d.cachet_count) > 0 ? parseInt(d.cachet_count) : 1,
      period: d.period || period,
      notes: d.notes || "",
      fees_included: d.fees_included !== undefined ? !!d.fees_included : true,
      contract_expenses: Array.isArray(d.contract_expenses) ? d.contract_expenses : [],
    });
    const im = intermittents.find(p => p.id === d.intermittent_id) || d.intermittent;
    setIntermittentQuery(im ? formatIntermittentName(im) : "");
    setDeclEditingId(d.id);
    setDeclDialogOpen(true);
    // Charge les frais uploadés par l'intermittent via son portail (lecture seule)
    setPortalExpenses([]);
    api.get(`/declarations/${d.id}/expenses`)
      .then(r => setPortalExpenses(r.data?.expenses || []))
      .catch(() => setPortalExpenses([]));
  };

  const intermittentLabel = (p) => formatIntermittentName(p);

  const onIntermittentQueryChange = (value) => {
    setIntermittentQuery(value);
    // Recherche exacte (cas du choix via datalist) sinon fuzzy pour capter une correspondance unique.
    const exact = intermittents.find(p => intermittentLabel(p) === value);
    if (exact) { setDeclField("intermittent_id", exact.id); return; }
    const q = value.trim().toLowerCase();
    if (!q) { setDeclField("intermittent_id", ""); return; }
    const matches = intermittents.filter(p =>
      formatIntermittentName(p).toLowerCase().includes(q)
    );
    setDeclField("intermittent_id", matches.length === 1 ? matches[0].id : "");
  };

  const setDeclField = (field, value) => {
    setDeclForm(prev => {
      const next = { ...prev, [field]: value };
      // Auto-sync de la période (mois de rattachement) sur la date de début.
      // Évite d'avoir une déclaration dans le mauvais mois du Kanban Déclarations.
      if (field === "start_date" && typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
        next.period = value.slice(0, 7);
        if (!prev.end_date || prev.end_date < value) next.end_date = value;
      }
      if (field === "role_type" && value === "artiste") {
        next.cachet_hours = 12;
      }
      if (field === "client_id") {
        const cli = clients.find(c => c.id === value);
        if (cli && cli.margin_rate !== undefined && cli.margin_rate !== null) {
          next.margin_rate = cli.margin_rate;
        }
      }
      if (field === "cost_ht_client" || field === "margin_rate" || field === "client_id") {
        const ht = parseFloat(next.cost_ht_client) || 0;
        const rate = parseFloat(next.margin_rate) || 0;
        next.cost_employer = +(ht - ht * rate / 100).toFixed(2);
      }
      return next;
    });
  };

  const onEventNameChange = (name) => {
    const match = events.find(e => e.name?.toLowerCase() === name.toLowerCase());
    setDeclForm(prev => ({
      ...prev,
      event_name: name,
      // Ne pas écraser event_id déjà positionné (pré-remplissage depuis Production)
      // tant que l'utilisateur n'a pas tapé un autre nom qui matche un autre événement.
      event_id: match ? match.id : prev.event_id,
      event_location: match?.location ? match.location : prev.event_location,
    }));
  };

  const saveDecl = async () => {
    try {
      if (!declForm.intermittent_id) { toast.error("Sélectionnez un intermittent"); return; }
      const payload = {
        ...declForm,
        type: "contrat",
        event_id: declForm.event_id || null,
        client_id: declForm.client_id || null,
        cost_ht_client: parseFloat(declForm.cost_ht_client) || 0,
        margin_rate: parseFloat(declForm.margin_rate) || 0,
        cachet_hours: declForm.role_type === "artiste" ? 12 : (parseFloat(declForm.cachet_hours) || 0),
        cachet_count: Math.max(1, parseInt(declForm.cachet_count) || 1),
      };
      if (declEditingId) await api.patch(`/declarations/${declEditingId}`, payload);
      else await api.post("/declarations", payload);
      toast.success(declEditingId ? "Modifiée" : "Créée");
      setDeclDialogOpen(false);
      load();
    } catch (e) { toast.error(extractError(e)); }
  };

  const removeDecl = async (d) => {
    if (!confirm("Supprimer cette déclaration ?")) return;
    try { await api.delete(`/declarations/${d.id}`); toast.success("Supprimée"); setDeclDialogOpen(false); load(); }
    catch (e) { toast.error(extractError(e)); }
  };

  const counts = summary?.counts || {};
  const totals = summary?.totals || { ht_client: 0, employer: 0, margin: 0 };
  const margin = declForm.cost_employer !== undefined
    ? (parseFloat(declForm.cost_ht_client) || 0) - (parseFloat(declForm.cost_employer) || 0)
    : 0;

  return (
    <div className="space-y-6" data-testid="declarations-page">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Déclarations</div>
          <h1 className="font-brand text-3xl md:text-4xl tracking-tight">Suivi des déclarations</h1>
          <p className="text-sm text-muted-foreground mt-1">Pipeline mensuel</p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <Button className="rounded-full bg-secondary text-secondary-foreground hover:bg-secondary/90 shadow-glow active:scale-95" onClick={openNewDecl} data-testid="add-decl-btn"><Plus className="h-4 w-4 mr-2" /> Nouvelle déclaration</Button>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <Button variant="outline" size="icon" className="rounded-full" onClick={() => shiftMonth(-1)} data-testid="prev-month"><ChevronLeft className="h-4 w-4" /></Button>
            <div className="font-display font-semibold text-lg min-w-[180px] text-center capitalize" data-testid="current-period">{label}</div>
            <Button variant="outline" size="icon" className="rounded-full" onClick={() => shiftMonth(1)} data-testid="next-month"><ChevronRight className="h-4 w-4" /></Button>
            <Button variant="outline" className="rounded-full ml-2" onClick={syncNow} disabled={syncing} data-testid="gmail-sync-btn">
              <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? "animate-spin" : ""}`} /> Synchroniser
            </Button>
            <Button variant="ghost" size="icon" className="rounded-full" onClick={() => setGmailOpen(true)} data-testid="gmail-settings-btn"><Settings className="h-4 w-4" /></Button>
            <Button variant="outline" className="rounded-full ml-2" onClick={openClientLinks} data-testid="request-link-btn">
              <Link2 className="h-4 w-4 mr-2" /> Lien demande client
            </Button>
            <Button variant="outline" className="rounded-full" onClick={openOrphans} data-testid="orphan-expenses-btn" title="Frais liés à des contrats supprimés ou annulés">
              <AlertCircle className="h-4 w-4 mr-2" /> Frais orphelins
            </Button>
            <Button variant="outline" className="rounded-full" onClick={openDeletions} data-testid="deletions-log-btn" title="Journal des demandes supprimées">
              <FileText className="h-4 w-4 mr-2" /> Journal
            </Button>
          </div>
        </div>
      </header>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="border-border/60 shadow-warm"><CardContent className="p-5">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">Total déclarations</div>
          <div className="font-display text-3xl font-semibold tabular-nums mt-1" data-testid="count-total">{counts.total || 0}</div>
          {cancelledCount > 0 && (
            <div className="text-[11px] font-semibold text-red-300 mt-1 flex items-center gap-1" data-testid="count-cancelled">
              <XCircle className="h-3 w-3" /> {cancelledCount} annulée{cancelledCount > 1 ? "s" : ""}
            </div>
          )}
        </CardContent></Card>
        <Card className="border-border/60 shadow-warm"><CardContent className="p-5">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">Total HT client</div>
          <div className="font-display text-3xl font-semibold tabular-nums mt-1 text-primary">{totals.ht_client.toLocaleString("fr-FR")} €</div>
        </CardContent></Card>
        <Card className="border-border/60 shadow-warm"><CardContent className="p-5">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">Marge cumulée</div>
          <div className="font-display text-3xl font-semibold tabular-nums mt-1">{totals.margin.toLocaleString("fr-FR")} €</div>
        </CardContent></Card>
        <Card className="border-border/60 shadow-warm"><CardContent className="p-5">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">Coût employeur</div>
          <div className="font-display text-3xl font-semibold tabular-nums mt-1">{totals.employer.toLocaleString("fr-FR")} €</div>
        </CardContent></Card>
        <Card
          className={`border-border/60 shadow-warm cursor-pointer hover:-translate-y-0.5 transition-transform ${filters.invoice === "pending" ? "ring-2 ring-secondary" : ""}`}
          onClick={() => setFilters(f => ({ ...f, invoice: f.invoice === "pending" ? "" : "pending" }))}
          data-testid="card-unbilled"
        >
          <CardContent className="p-5">
            <div className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1">
              <FileText className="h-3 w-3" /> Restant à facturer
            </div>
            <div className="font-display text-3xl font-semibold tabular-nums mt-1 text-secondary-foreground bg-secondary rounded-md inline-block px-2">{unbilledCount}</div>
            <div className="text-[11px] text-muted-foreground mt-1 tabular-nums">{unbilledHt.toLocaleString("fr-FR")} € HT</div>
          </CardContent>
        </Card>
      </div>

      <div className="flex flex-wrap items-end gap-3 p-3 rounded-2xl bg-muted border border-border/60" data-testid="declaration-filters">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold flex items-center gap-1 pt-2">
          <Filter className="h-3 w-3" /> Filtres
        </div>
        <div className="flex-1 min-w-[220px]">
          <Label className="text-xs">Intermittent</Label>
          <Input
            list="filter-intermittent-suggest"
            value={filterIntermittentQuery}
            onChange={e => {
              const v = e.target.value;
              setFilterIntermittentQuery(v);
              const exact = intermittents.find(p => formatIntermittentName(p) === v);
              if (exact) { setFilters(f => ({ ...f, intermittent_id: exact.id })); return; }
              const q = v.trim().toLowerCase();
              if (!q) { setFilters(f => ({ ...f, intermittent_id: "" })); return; }
              const matches = intermittents.filter(p => formatIntermittentName(p).toLowerCase().includes(q));
              setFilters(f => ({ ...f, intermittent_id: matches.length === 1 ? matches[0].id : "" }));
            }}
            placeholder="Tous — tapez pour filtrer..."
            autoComplete="off"
            className={`h-10 ${filters.intermittent_id ? "border-primary/60" : ""}`}
            data-testid="filter-intermittent"
          />
          <datalist id="filter-intermittent-suggest">
            {intermittents.map(p => (
              <option key={p.id} value={formatIntermittentName(p)} />
            ))}
          </datalist>
        </div>
        <div className="flex-1 min-w-[200px]">
          <Label className="text-xs">Client</Label>
          <Select value={filters.client_id || "all"} onValueChange={v => setFilters(f => ({ ...f, client_id: v === "all" ? "" : v }))}>
            <SelectTrigger data-testid="filter-client"><SelectValue placeholder="Tous" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous</SelectItem>
              {clients.map(c => (
                <SelectItem key={c.id} value={c.id}>
                  <span className="inline-flex items-center gap-2">
                    <span className="inline-block w-3 h-3 rounded-full" style={{ backgroundColor: c.color || "#134FE4" }} />
                    {c.name}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1 min-w-[200px]">
          <Label className="text-xs">Événement</Label>
          <Select value={filters.event_id || "all"} onValueChange={v => setFilters(f => ({ ...f, event_id: v === "all" ? "" : v }))}>
            <SelectTrigger data-testid="filter-event"><SelectValue placeholder="Tous" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous</SelectItem>
              {events.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1 min-w-[200px]">
          <Label className="text-xs">Poste sur la mission</Label>
          <Input
            list="filter-role-suggest"
            value={filters.role}
            onChange={e => setFilters(f => ({ ...f, role: e.target.value }))}
            placeholder="Tous — tapez pour filtrer..."
            autoComplete="off"
            className={`h-10 ${filters.role ? "border-primary/60" : ""}`}
            data-testid="filter-role"
          />
          <datalist id="filter-role-suggest">
            {roleSuggestions.map(r => <option key={r} value={r} />)}
          </datalist>
        </div>
        <div className="flex-1 min-w-[180px]">
          <Label className="text-xs">Facturation</Label>
          <Select value={filters.invoice || "all"} onValueChange={v => setFilters(f => ({ ...f, invoice: v === "all" ? "" : v }))}>
            <SelectTrigger data-testid="filter-invoice"><SelectValue placeholder="Toutes" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes</SelectItem>
              <SelectItem value="pending">À facturer</SelectItem>
              <SelectItem value="sent">Facturées</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-[150px]">
          <Label className="text-xs">Contrat du</Label>
          <Input
            type="date"
            value={filters.date_from}
            onChange={e => setFilters(f => ({ ...f, date_from: e.target.value }))}
            className="h-10"
            data-testid="filter-date-from"
          />
        </div>
        <div className="min-w-[150px]">
          <Label className="text-xs">au</Label>
          <Input
            type="date"
            value={filters.date_to}
            min={filters.date_from || undefined}
            onChange={e => setFilters(f => ({ ...f, date_to: e.target.value }))}
            className="h-10"
            data-testid="filter-date-to"
          />
        </div>
        <div className="text-xs text-muted-foreground tabular-nums pb-2">
          <span className="font-semibold text-foreground">{filteredCount}</span> / {declarations.length} déclaration(s)
        </div>
        {hasActiveFilter && (
          <Button
            variant="ghost"
            size="sm"
            className="rounded-full mb-1"
            onClick={() => { setFilters({ intermittent_id: "", client_id: "", event_id: "", invoice: "", date_from: "", date_to: "", role: "" }); setFilterIntermittentQuery(""); }}
            data-testid="filter-reset"
          >
            <X className="h-3.5 w-3.5 mr-1" /> Réinitialiser
          </Button>
        )}
      </div>

      <div className="grid grid-flow-col auto-cols-[minmax(240px,1fr)] gap-3 overflow-x-auto pb-4">
        {PHASES.map((p, idx) => {
          const cards = grouped[p.v] || [];
          const idsInCol = cards.map(c => c.id);
          const selectedInCol = idsInCol.filter(id => selectedIds[id]).length;
          const allSelected = idsInCol.length > 0 && selectedInCol === idsInCol.length;
          const nextPhase = PHASES[idx + 1];
          return (
            <div
              key={p.v}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => onDrop(p.v, e)}
              className="rounded-2xl bg-muted border border-border/60 p-3 min-h-[400px] flex flex-col"
              data-testid={`column-${p.v}`}
            >
              <div className="flex items-center justify-between mb-2 px-1">
                <div className="flex items-center gap-2">
                  <Badge className={`${p.color} rounded-full border text-xs`}>{p.short}</Badge>
                  <span className="text-xs text-muted-foreground tabular-nums">{cards.length}</span>
                </div>
              </div>
              {cards.length > 0 && (
                <div className="flex flex-col gap-1.5 mb-2 px-1 py-1.5 rounded-lg bg-card border border-border/50 min-w-0">
                  <label className="flex items-center gap-2 text-[11px] font-medium cursor-pointer select-none min-w-0">
                    <Checkbox
                      checked={allSelected}
                      onCheckedChange={() => selectAllInColumn(p.v)}
                      data-testid={`select-all-${p.v}`}
                    />
                    <span className="truncate">Tout {allSelected ? "désélectionner" : "sélectionner"}</span>
                    {selectedInCol > 0 && <span className="text-primary tabular-nums shrink-0">({selectedInCol})</span>}
                  </label>
                  {selectedInCol > 0 && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          size="sm"
                          variant="secondary"
                          className="rounded-full h-7 px-2 text-[11px] w-full justify-center"
                          data-testid={`move-menu-${p.v}`}
                          title={`Passer ${selectedInCol} carte(s) vers une autre colonne`}
                        >
                          <CheckCheck className="h-3 w-3 mr-1" /> Passer <ArrowRight className="h-3 w-3 mx-1" /> …
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="z-[100]" data-testid={`move-menu-content-${p.v}`}>
                        <DropdownMenuLabel className="text-[11px]">Déplacer {selectedInCol} carte(s) vers</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        {PHASES.filter(t => t.v !== p.v).map(t => (
                          <DropdownMenuItem
                            key={t.v}
                            onClick={() => moveSelectedTo(p.v, t.v)}
                            className="text-xs cursor-pointer"
                            data-testid={`move-to-${p.v}-${t.v}`}
                          >
                            <ArrowRight className="h-3.5 w-3.5 mr-2 opacity-70" /> {t.label}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                  {selectedInCol > 0 && p.v !== "annule" && (
                    <div className="flex flex-wrap items-center gap-1.5">
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-full h-7 px-2 text-[10px] border-primary/40 text-primary hover:bg-primary/10"
                        onClick={() => bulkInvoiceInColumn(p.v, "sent")}
                        data-testid={`bulk-invoice-sent-${p.v}`}
                        title={`Marquer ${selectedInCol} facture(s) comme envoyée(s)`}
                      >
                        <FileText className="h-3 w-3 mr-1" /> Facture envoyée
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-full h-7 px-2 text-[10px] border-emerald-500/50 text-emerald-300 hover:bg-emerald-500/15"
                        onClick={() => bulkInvoiceInColumn(p.v, "paid")}
                        data-testid={`bulk-invoice-paid-${p.v}`}
                        title={`Marquer ${selectedInCol} facture(s) comme payée(s)`}
                      >
                        <Euro className="h-3 w-3 mr-1" /> Facture payée
                      </Button>
                    </div>
                  )}
                </div>
              )}
              <div className="space-y-2 flex-1">
                {cards.map(d => (
                  <DeclCard
                    key={d.id}
                    d={d}
                    onOpen={openEditDecl}
                    onDragStart={onDragStart}
                    selected={!!selectedIds[d.id]}
                    onToggleSelect={toggleSelect}
                    onToggleInvoice={toggleInvoice}
                    onApproveChange={approveChange}
                    onRejectChange={openRejectDialog}
                    onDeleteChange={deleteChange}
                  />
                ))}
                {cards.length === 0 && (
                  <div className="text-[11px] text-muted-foreground text-center py-6 border-2 border-dashed border-border/60 rounded-xl">
                    Glissez une carte ici
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Deletions log dialog (admin/manager) */}
      <Dialog open={deletionsOpen} onOpenChange={setDeletionsOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="deletions-log-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" /> Journal des demandes supprimées
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm text-muted-foreground bg-muted border border-border/50 rounded-lg p-3 mb-3">
            Historique des demandes de modification/annulation supprimées par un admin ou manager. Utile pour retrouver qui a nettoyé quelle demande et quand.
          </div>
          {deletionsLoading ? (
            <div className="py-10 text-center text-muted-foreground">Chargement…</div>
          ) : deletions.length === 0 ? (
            <div className="py-10 text-center text-muted-foreground">Aucune suppression enregistrée.</div>
          ) : (
            <div className="flex-1 overflow-y-auto border border-border/60 rounded-lg divide-y divide-border/60">
              {deletions.map(d => (
                <div key={d.id} className="p-3 text-sm" data-testid={`deletion-row-${d.id}`}>
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge className={`rounded-full text-[10px] font-semibold border ${d.cr_action === "cancel" ? "bg-red-500/15 text-red-200 border-red-500/50" : "bg-blue-500/15 text-blue-200 border-blue-500/50"}`}>
                        {d.cr_action === "cancel" ? "Annulation" : "Modification"}
                      </Badge>
                      <span className="text-xs text-muted-foreground">État avant suppression :</span>
                      <Badge variant="outline" className="rounded-full text-[10px]">
                        {d.cr_status === "pending" ? "en attente" : d.cr_status === "handled" ? "approuvée" : d.cr_status === "rejected" ? "refusée" : d.cr_status || "—"}
                      </Badge>
                      {d.restored_at && (
                        <Badge className="rounded-full text-[10px] bg-emerald-500/15 text-emerald-200 border-emerald-400 font-semibold">
                          <RotateCcw className="h-3 w-3 mr-1" /> Restaurée
                        </Badge>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground tabular-nums">
                      Le {d.timestamp?.slice(8, 10)}/{d.timestamp?.slice(5, 7)}/{d.timestamp?.slice(0, 4)} · {d.timestamp?.slice(11, 16)}
                    </div>
                  </div>
                  <div className="mt-1.5 text-sm">
                    <span className="font-semibold">{d.intermittent_name || "—"}</span>
                    {d.event_name && <span className="text-muted-foreground"> · {d.event_name}</span>}
                    {d.event_date && <span className="text-muted-foreground"> · {d.event_date}</span>}
                    {d.client_name && <span className="text-muted-foreground"> · {d.client_name}</span>}
                  </div>
                  {d.cr_message && (
                    <div className="mt-1 text-xs italic text-muted-foreground bg-muted rounded px-2 py-1 line-clamp-2">« {d.cr_message} »</div>
                  )}
                  <div className="mt-1 flex items-center justify-between gap-2 flex-wrap">
                    <div className="text-[11px] text-muted-foreground">
                      Supprimée par <span className="font-semibold text-foreground">{d.by_user?.name || d.by_user?.email || "—"}</span>
                      {d.restored_at && (
                        <span> · Restaurée par <span className="font-semibold text-emerald-300">{d.restored_by_user?.name || d.restored_by_user?.email || "—"}</span> le {d.restored_at.slice(8,10)}/{d.restored_at.slice(5,7)}/{d.restored_at.slice(0,4)}</span>
                      )}
                    </div>
                    {!d.restored_at && d.cr_snapshot && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-full h-7 px-2 text-[11px] border-emerald-500/60 text-emerald-300 hover:bg-emerald-500/15"
                        onClick={() => restoreDeletion(d)}
                        data-testid={`restore-deletion-${d.id}`}
                      >
                        <RotateCcw className="h-3 w-3 mr-1" /> Restaurer
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          <DialogFooter className="pt-3">
            <Button variant="outline" className="rounded-full" onClick={() => setDeletionsOpen(false)}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject change-request dialog */}
      <Dialog open={rejectDialog.open} onOpenChange={v => !v && setRejectDialog({ open: false, decl: null, change: null, reason: "" })}>
        <DialogContent className="max-w-md" data-testid="reject-change-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <XCircle className="h-5 w-5 text-destructive" /> Refuser la demande client
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="text-xs text-muted-foreground bg-muted border border-border/50 rounded-md px-3 py-2">
              Cette demande d'<strong>{rejectDialog.change?.action === "cancel" ? "annulation" : "modification"}</strong> sera marquée refusée et le motif sera visible dans le portail du client.
            </div>
            {rejectDialog.change?.message && (
              <div className="text-xs bg-amber-500/15 border border-amber-500/40 rounded-md px-3 py-2">
                <div className="font-semibold text-amber-200 mb-1">Message du client&nbsp;:</div>
                <div className="italic text-amber-200 whitespace-pre-line">« {rejectDialog.change.message} »</div>
              </div>
            )}
            <div>
              <Label className="text-xs">Motif du refus <span className="text-destructive">*</span></Label>
              <Textarea
                value={rejectDialog.reason}
                onChange={e => setRejectDialog(d => ({ ...d, reason: e.target.value }))}
                placeholder="Ex : la date de l'événement est passée, contactez-nous par téléphone."
                rows={3}
                data-testid="reject-reason-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setRejectDialog({ open: false, decl: null, change: null, reason: "" })}>Annuler</Button>
            <Button
              className="rounded-full bg-destructive hover:bg-destructive/90 text-white"
              onClick={submitReject}
              disabled={rejecting || !rejectDialog.reason.trim()}
              data-testid="reject-submit-btn"
            >
              <XCircle className="h-4 w-4 mr-2" /> {rejecting ? "Envoi…" : "Refuser la demande"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Orphan expenses dialog (admin) */}
      <Dialog open={orphansOpen} onOpenChange={setOrphansOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="orphan-expenses-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-amber-600" /> Frais orphelins
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm text-muted-foreground bg-amber-500/15 border border-amber-500/50 rounded-lg p-3 mb-3">
            Ces frais sont rattachés à des contrats qui n'existent plus (supprimés) ou annulés. Ils ne sont plus visibles dans les portails, mais restent dans la base tant qu'ils ne sont pas purgés.
          </div>
          {orphansLoading ? (
            <div className="py-10 text-center text-muted-foreground">Chargement…</div>
          ) : orphans.count === 0 ? (
            <div className="py-10 text-center text-emerald-300 font-semibold">
              <CheckCheck className="h-5 w-5 mx-auto mb-2" /> Aucun frais orphelin. Tout est propre.
            </div>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-3 mb-3">
                <div className="rounded-lg border border-border/60 p-3 bg-card">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Frais orphelins</div>
                  <div className="font-display text-3xl font-semibold tabular-nums text-amber-300">{orphans.count}</div>
                </div>
                <div className="rounded-lg border border-border/60 p-3 bg-card">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Montant total</div>
                  <div className="font-display text-3xl font-semibold tabular-nums">{Number(orphans.total_amount || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</div>
                </div>
                <div className="rounded-lg border border-border/60 p-3 bg-card">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Km voiture perso</div>
                  <div className="font-display text-3xl font-semibold tabular-nums">{Number(orphans.total_km || 0).toLocaleString("fr-FR", { maximumFractionDigits: 0 })}</div>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto border border-border/60 rounded-lg divide-y divide-border/60">
                {orphans.orphans.map(o => (
                  <div key={o.id} className="p-3 flex items-center gap-3 text-sm" data-testid={`orphan-row-${o.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold truncate">{o.title || o.expense_label || o.expense_type}</div>
                      <div className="text-xs text-muted-foreground flex flex-wrap gap-x-2">
                        <span>{o.intermittent_name}</span>
                        <span>· {o.period || "—"}</span>
                        <span>· {o.orphan_reason === "declaration_supprimée" ? "contrat supprimé" : o.orphan_reason === "declaration_annulée" ? "contrat annulé" : "sans contrat"}</span>
                        {o.expense_type === "transport_voiture" && Number(o.km_count || 0) > 0 && (
                          <span>· {Number(o.km_count).toLocaleString("fr-FR")} km</span>
                        )}
                      </div>
                    </div>
                    <div className="text-right tabular-nums font-semibold">
                      {Number(o.amount || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
          <DialogFooter className="pt-3">
            <Button variant="outline" className="rounded-full" onClick={() => setOrphansOpen(false)}>Fermer</Button>
            {orphans.count > 0 && (
              <Button
                className="rounded-full bg-red-600 hover:bg-red-700 text-white"
                onClick={purgeOrphans}
                disabled={purging}
                data-testid="purge-orphans-btn"
              >
                <Trash2 className="h-4 w-4 mr-2" /> {purging ? "Purge en cours…" : `Purger les ${orphans.count} frais orphelin(s)`}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Client request links dialog */}
      <Dialog open={clientLinksOpen} onOpenChange={setClientLinksOpen}>
        <DialogContent className="max-w-3xl" data-testid="client-links-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Building2 className="h-5 w-5" /> Liens de demande client</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-muted-foreground bg-muted rounded-lg p-3 mb-3">
            Générez un lien unique par client. Le formulaire ouvert par ce lien pré-remplit et verrouille les informations du client (dénomination, contact, taux de marge). Chaque envoi crée une déclaration en « Contrat à faire ».
          </div>

          <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2">Clients</div>
          <div className="space-y-2 max-h-[26rem] overflow-y-auto">
            {clients.length === 0 && (
              <div className="text-sm text-muted-foreground text-center py-6">
                Aucun client. Créez-en un dans l'onglet Clients.
              </div>
            )}
            {clients.map(c => {
              const existing = publicLinks.find(l => l.type === "declaration_request" && l.client_id === c.id);
              return (
                <div key={c.id} className="border border-border/60 rounded-lg p-3 flex items-center gap-3" data-testid={`client-link-row-${c.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate flex items-center gap-2">
                      {c.name}
                      {existing && !existing.active && <Badge variant="outline" className="text-[10px] rounded-full text-destructive border-destructive/40">Inactif</Badge>}
                      {!existing && <Badge variant="outline" className="text-[10px] rounded-full">Pas encore de lien</Badge>}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      {c.contact_name ? `Contact : ${c.contact_name}` : "—"} · Marge {c.margin_rate ?? 20}%
                      {existing ? ` · ${existing.used_count || 0} soumission(s)` : ""}
                    </div>
                    {existing && (
                      <div className="text-[11px] text-muted-foreground truncate font-mono">{existing.url}</div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {existing && (
                      <Checkbox checked={!!existing.active} onCheckedChange={() => toggleLinkActive(existing)} data-testid={`client-toggle-${c.id}`} />
                    )}
                    {existing ? (
                      <>
                        <Button variant="outline" size="icon" className="rounded-full" onClick={() => editLinkSlug(existing)} title="Modifier l'URL personnalisée" data-testid={`client-slug-${c.id}`}><Pencil className="h-4 w-4" /></Button>
                        <Button variant="outline" size="icon" className="rounded-full" onClick={() => copyLink(existing.url)} data-testid={`client-copy-${c.id}`}><Copy className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="icon" className="text-destructive hover:bg-destructive/10" onClick={() => removePublicLink(existing.token)} data-testid={`client-del-${c.id}`}><Trash2 className="h-4 w-4" /></Button>
                      </>
                    ) : (
                      <Button className="rounded-full bg-primary active:scale-95" onClick={() => createOrGetClientLink(c)} data-testid={`client-create-${c.id}`}>
                        <Plus className="h-4 w-4 mr-2" /> Créer & copier
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setClientLinksOpen(false)}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Declaration dialog */}
      <Dialog open={declDialogOpen} onOpenChange={setDeclDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col p-0" data-testid="decl-dialog">
          <DialogHeader className="px-6 pt-6 pb-3 border-b border-border/40 shrink-0">
            <DialogTitle>{declEditingId ? "Modifier la déclaration" : "Nouvelle déclaration"}</DialogTitle>
          </DialogHeader>
          <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Statut (colonne Kanban) *</Label>
              <Select value={declForm.status} onValueChange={v => setDeclField("status", v)}>
                <SelectTrigger data-testid="d-status"><SelectValue /></SelectTrigger>
                <SelectContent>{PHASES.map(p => <SelectItem key={p.v} value={p.v}>{p.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Intermittent *</Label>
              <Input
                list="intermittent-suggest"
                value={intermittentQuery}
                onChange={e => onIntermittentQueryChange(e.target.value)}
                placeholder="Tapez pour filtrer par nom / poste..."
                autoComplete="off"
                data-testid="d-person"
                className={declForm.intermittent_id ? "border-primary/60" : ""}
              />
              <datalist id="intermittent-suggest">
                {intermittents.map(p => (
                  <option key={p.id} value={intermittentLabel(p)} />
                ))}
              </datalist>
              {declForm.intermittent_id ? (
                <div className="text-[11px] text-primary mt-1">✓ Sélectionné</div>
              ) : intermittentQuery ? (
                <div className="text-[11px] text-muted-foreground mt-1">Continuez à taper pour trouver une correspondance unique</div>
              ) : null}
            </div>

            <div><Label>Date de début *</Label><Input type="date" value={declForm.start_date} onChange={e => setDeclField("start_date", e.target.value)} data-testid="d-start" /></div>
            <div><Label>Date de fin *</Label><Input type="date" value={declForm.end_date} onChange={e => setDeclField("end_date", e.target.value)} data-testid="d-end" /></div>

            <div><Label>Heure de prise de poste</Label><Input type="time" value={declForm.start_time} onChange={e => setDeclField("start_time", e.target.value)} data-testid="d-start-time" /></div>
            <div><Label>Nombre de cachets *</Label>
              <Input
                type="number"
                min="1"
                step="1"
                value={declForm.cachet_count}
                onKeyDown={e => { if (["e", "E", "+", "-", ".", ","].includes(e.key)) e.preventDefault(); }}
                onChange={e => setDeclField("cachet_count", e.target.value.replace(/[^0-9]/g, ""))}
                data-testid="d-cachet-count"
              />
              <div className="text-[11px] text-muted-foreground mt-1">Nombre de missions à déclarer.</div>
            </div>
            <div><Label>Durée d'un cachet (heures)</Label>
              <Input
                type="number"
                min="0"
                step="0.5"
                value={declForm.role_type === "artiste" ? 12 : declForm.cachet_hours}
                onChange={e => setDeclField("cachet_hours", e.target.value)}
                placeholder="ex : 8"
                readOnly={declForm.role_type === "artiste"}
                className={declForm.role_type === "artiste" ? "bg-muted" : ""}
                data-testid="d-cachet"
              />
              {declForm.role_type === "artiste" && <div className="text-[11px] text-muted-foreground mt-1">Forfait artiste — 12 h imposées.</div>}
            </div>

            <div className="md:col-span-2 border-t border-border/50 pt-3">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-2 flex items-center gap-1"><Building2 className="h-3 w-3" /> Client & poste</div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Client (couleur du bloc)</Label>
                  <Select value={declForm.client_id || "none"} onValueChange={v => setDeclField("client_id", v === "none" ? "" : v)}>
                    <SelectTrigger data-testid="d-client"><SelectValue placeholder="Aucun" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Aucun</SelectItem>
                      {clients.map(c => (
                        <SelectItem key={c.id} value={c.id}>
                          <span className="inline-flex items-center gap-2">
                            <span className="inline-block w-3 h-3 rounded-full" style={{ backgroundColor: c.color || "#134FE4" }} />
                            {c.name}{c.margin_rate !== undefined ? ` · ${c.margin_rate}%` : ""}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Type</Label>
                  <Select value={declForm.role_type} onValueChange={v => setDeclField("role_type", v)}>
                    <SelectTrigger data-testid="d-role-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="technicien">Technicien</SelectItem>
                      <SelectItem value="artiste">Artiste</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label>Poste sur la mission</Label>
                  <Input
                    list="poste-suggest"
                    value={declForm.role}
                    onChange={e => setDeclField("role", e.target.value)}
                    placeholder="Régisseur son, Chorégraphe, Danseur..."
                    data-testid="d-role"
                  />
                  <datalist id="poste-suggest">
                    {roleSuggestions.map(r => <option key={r} value={r} />)}
                  </datalist>
                </div>
              </div>
            </div>

            <div className="md:col-span-2 border-t border-border/50 pt-3">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-2 flex items-center gap-1"><Euro className="h-3 w-3" /> Facturation & marge</div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Coût HT client (€)</Label>
                  <Input type="number" min="0" step="0.01" value={declForm.cost_ht_client}
                         onChange={e => setDeclField("cost_ht_client", e.target.value)} data-testid="d-ht" />
                </div>
                <div>
                  <Label>Taux de marge (%)</Label>
                  <Input type="number" min="0" max="100" step="0.1" value={declForm.margin_rate}
                         onChange={e => setDeclField("margin_rate", e.target.value)} data-testid="d-margin-rate" />
                </div>
                <div>
                  <Label>Coût employeur (€)</Label>
                  <Input type="number" value={declForm.cost_employer} readOnly
                         className="bg-muted font-semibold tabular-nums" data-testid="d-employer" />
                </div>
              </div>
              <div className="text-xs text-muted-foreground mt-2 tabular-nums">
                Marge = <span className="font-semibold text-foreground">{margin.toFixed(2)} €</span> · Coût employeur = HT client × (1 − taux)
              </div>
              <div className="mt-3">
                <Label>Traitement des frais</Label>
                <div className="flex gap-2 mt-1" data-testid="d-fees">
                  <label className={`flex-1 flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer select-none text-sm ${declForm.fees_included ? "border-primary bg-primary/5 text-primary font-semibold" : "border-border"}`}>
                    <input type="radio" name="d-fees" checked={!!declForm.fees_included} onChange={() => setDeclField("fees_included", true)} data-testid="d-fees-included" />
                    Frais inclus dans le HT
                  </label>
                  <label className={`flex-1 flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer select-none text-sm ${!declForm.fees_included ? "border-primary bg-primary/5 text-primary font-semibold" : "border-border"}`}>
                    <input type="radio" name="d-fees" checked={!declForm.fees_included} onChange={() => setDeclField("fees_included", false)} data-testid="d-fees-excluded" />
                    Frais facturés en sus
                  </label>
                </div>
              </div>
            </div>

            <div className="md:col-span-2 border-t border-border/50 pt-3">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-2 flex items-center justify-between gap-1">
                <span className="flex items-center gap-1"><FileText className="h-3 w-3" /> Frais du contrat</span>
                <Button type="button" variant="outline" size="sm" className="rounded-full h-7 text-[11px]"
                  onClick={() => setDeclField("contract_expenses", [...(declForm.contract_expenses || []), { title: "", category: "", amount: "" }])}
                  data-testid="d-add-frais"
                >+ Ajouter un frais</Button>
              </div>
              <div className="space-y-2" data-testid="d-frais-list">
                {(declForm.contract_expenses || []).length === 0 && portalExpenses.length === 0 && (
                  <div className="text-xs text-muted-foreground italic">Aucun frais renseigné.</div>
                )}
                {/* Frais uploadés par l'intermittent via son portail — lecture seule pour l'admin */}
                {portalExpenses.length > 0 && (
                  <div className="rounded-lg border-2 border-primary/30 bg-primary/5 p-2 space-y-1" data-testid="d-portal-frais">
                    <div className="flex items-center justify-between">
                      <div className="text-[10px] uppercase tracking-widest text-primary font-bold flex items-center gap-1">
                        <Upload className="h-3 w-3" /> Frais déposés par l'intermittent ({portalExpenses.length})
                      </div>
                      <div className="text-[11px] tabular-nums font-semibold text-primary">
                        {portalExpenses.reduce((s, e) => s + (parseFloat(e.amount) || 0), 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                      </div>
                    </div>
                    <div className="divide-y divide-primary/20">
                      {portalExpenses.map(e => (
                        <div key={e.id} className="flex items-center gap-2 py-1.5 text-xs" data-testid={`d-portal-frais-row-${e.id}`}>
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold truncate">{e.title}</div>
                            <div className="text-[10px] text-muted-foreground">{e.expense_label || e.expense_type}{e.expense_type === "transport_voiture" && ` · ${e.km_count} km × ${Number(e.cost_per_km).toFixed(2)}€/km`}</div>
                          </div>
                          {e.receipt_file_id && (
                            <a href={`${API_BASE}/files/${e.receipt_file_id}`} target="_blank" rel="noreferrer" className="text-primary underline text-[10px] inline-flex items-center gap-1" title="Voir le justificatif">
                              <FileText className="h-3 w-3" /> Justif
                            </a>
                          )}
                          <div className="font-bold tabular-nums shrink-0">{Number(e.amount).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {(declForm.contract_expenses || []).map((exp, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-end p-2 rounded-lg border border-border/60 bg-muted" data-testid={`d-frais-row-${i}`}>
                    <div className="col-span-12 md:col-span-5">
                      <Label className="text-[10px]">Nom du frais</Label>
                      <Input value={exp.title || ""} onChange={e => {
                        const arr = [...declForm.contract_expenses]; arr[i] = { ...arr[i], title: e.target.value }; setDeclField("contract_expenses", arr);
                      }} placeholder="Ex : Ticket TGV Paris-Lyon" data-testid={`d-frais-title-${i}`} />
                    </div>
                    <div className="col-span-8 md:col-span-4">
                      <Label className="text-[10px]">Catégorie</Label>
                      <select value={exp.category || ""} onChange={e => {
                        const arr = [...declForm.contract_expenses]; arr[i] = { ...arr[i], category: e.target.value }; setDeclField("contract_expenses", arr);
                      }} className="w-full h-10 border border-border rounded-md px-3 bg-background text-sm" data-testid={`d-frais-cat-${i}`}>
                        <option value="">— Choisir —</option>
                        {DECL_EXPENSE_CATS.map(c => <option key={c.v} value={c.v}>{c.label}</option>)}
                      </select>
                    </div>
                    <div className="col-span-3 md:col-span-2">
                      <Label className="text-[10px]">Montant (€)</Label>
                      <Input type="number" min="0" step="0.01" value={exp.amount ?? ""} onChange={e => {
                        const arr = [...declForm.contract_expenses]; arr[i] = { ...arr[i], amount: e.target.value }; setDeclField("contract_expenses", arr);
                      }} data-testid={`d-frais-amount-${i}`} />
                    </div>
                    <div className="col-span-1 flex justify-end">
                      <Button type="button" variant="ghost" size="icon" className="h-9 w-9 text-destructive hover:bg-destructive/10" onClick={() => {
                        const arr = [...declForm.contract_expenses]; arr.splice(i, 1); setDeclField("contract_expenses", arr);
                      }} data-testid={`d-frais-del-${i}`}>×</Button>
                    </div>
                  </div>
                ))}
                {((declForm.contract_expenses || []).length > 0 || portalExpenses.length > 0) && (
                  <div className="text-right text-xs">
                    <span className="text-muted-foreground">Total frais&nbsp;: </span>
                    <span className="font-brand text-lg text-primary tabular-nums">
                      {(
                        (declForm.contract_expenses || []).reduce((s, e) => s + (parseFloat(e.amount) || 0), 0)
                        + portalExpenses.reduce((s, e) => s + (parseFloat(e.amount) || 0), 0)
                      ).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                    </span>
                    <span className={`ml-2 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${declForm.fees_included ? "bg-emerald-500/15 text-emerald-200" : "bg-secondary/40 text-primary"}`}>
                      {declForm.fees_included ? "Inclus dans le HT" : "Facturés en sus"}
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div className="md:col-span-2 border-t border-border/50 pt-3">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-2 flex items-center gap-1"><CalIcon className="h-3 w-3" /> Événement rattaché</div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Nom de l'événement</Label>
                  <Input
                    list="ev-suggest"
                    value={declForm.event_name}
                    onChange={e => onEventNameChange(e.target.value)}
                    placeholder="Tapez pour rechercher ou saisir librement..."
                    data-testid="d-event-name"
                  />
                  <datalist id="ev-suggest">
                    {events.map(e => <option key={e.id} value={e.name} />)}
                  </datalist>
                  {declForm.event_id && (
                    <div className="text-[11px] text-primary mt-1">✓ Lié à un événement existant</div>
                  )}
                </div>
                <div>
                  <Label>Lieu de l'événement</Label>
                  <Input value={declForm.event_location} onChange={e => setDeclField("event_location", e.target.value)} data-testid="d-event-location" />
                </div>
              </div>
            </div>

            <div className="md:col-span-2"><Label>Notes</Label><Textarea rows={3} value={declForm.notes} onChange={e => setDeclField("notes", e.target.value)} data-testid="d-notes" /></div>
          </div>
          </div>
          <DialogFooter className="px-6 py-3 border-t border-border/40 shrink-0">
            {declEditingId && (
              <>
                <Button variant="ghost" className="text-destructive mr-auto" onClick={() => { const d = declarations.find(x => x.id === declEditingId); if (d) removeDecl(d); }}>
                  <Trash2 className="h-4 w-4 mr-2" /> Supprimer
                </Button>
                <Button
                  variant="outline"
                  className="rounded-full border-primary/40 text-primary hover:bg-primary/5"
                  onClick={() => {
                    // Duplication : passe en mode "création" tout en gardant le formulaire pré-rempli.
                    setDeclEditingId(null);
                    toast.success("Duplication — modifiez les paramètres puis enregistrez pour créer une nouvelle déclaration");
                  }}
                  data-testid="duplicate-decl-btn"
                >
                  <Copy className="h-4 w-4 mr-2" /> Dupliquer la déclaration
                </Button>
              </>
            )}
            <Button variant="outline" className="rounded-full" onClick={() => setDeclDialogOpen(false)}>Annuler</Button>
            <Button className="rounded-full active:scale-95 bg-primary" onClick={saveDecl} data-testid="confirm-decl">{declEditingId ? "Enregistrer" : "Créer"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Gmail settings dialog */}
      <Dialog open={gmailOpen} onOpenChange={setGmailOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Mail className="h-5 w-5" /> Auto-détection contrats signés</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="text-sm text-muted-foreground space-y-2 bg-muted rounded-lg p-3">
              <p>Configurez l'accès à la boîte <strong>contact@antoineevenements.fr</strong> pour que l'app déplace automatiquement les cartes vers <strong>Signé</strong> quand un mail de <code>signature@ghs.fr</code> arrive.</p>
              <p><strong>Comment obtenir un mot de passe d'application Google :</strong></p>
              <ol className="list-decimal ml-5 space-y-1">
                <li>Activez la validation en 2 étapes sur le compte Google</li>
                <li>Allez sur <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" className="text-primary underline">myaccount.google.com/apppasswords</a></li>
                <li>Créez un mot de passe d'application (nom : "AAE app")</li>
                <li>Copiez le code 16 caractères et collez-le ci-dessous</li>
              </ol>
            </div>

            <div><Label>Adresse Gmail à surveiller *</Label>
              <Input type="email" value={gmailForm.email} onChange={e => setGmailForm({ ...gmailForm, email: e.target.value })} placeholder="contact@antoineevenements.fr" data-testid="gmail-email" />
            </div>
            <div><Label>Mot de passe d'application (16 caractères)</Label>
              <Input type="password" value={gmailForm.app_password} onChange={e => setGmailForm({ ...gmailForm, app_password: e.target.value })} placeholder={gmailConfig?.configured ? "•••• (laissez vide pour ne pas changer)" : "xxxx xxxx xxxx xxxx"} data-testid="gmail-app-pw" />
              <div className="text-xs text-muted-foreground mt-1">Différent de votre mot de passe Gmail principal.</div>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox id="gmail-enabled" checked={gmailForm.enabled} onCheckedChange={v => setGmailForm({ ...gmailForm, enabled: !!v })} data-testid="gmail-enabled" />
              <Label htmlFor="gmail-enabled" className="cursor-pointer">Vérification automatique activée (toutes les 15 min)</Label>
            </div>

            {gmailConfig?.last_sync_at && (
              <div className="border-t border-border/50 pt-3 text-xs space-y-1">
                <div className="font-semibold text-muted-foreground uppercase tracking-wider">Dernière synchro</div>
                <div className="text-muted-foreground">{new Date(gmailConfig.last_sync_at).toLocaleString("fr-FR")}</div>
                {gmailConfig.last_sync_result && (
                  <div className="text-muted-foreground">
                    {gmailConfig.last_sync_result.scanned || 0} email(s) analysé(s) · {gmailConfig.last_sync_result.moved || 0} carte(s) déplacée(s)
                    {gmailConfig.last_sync_result.unmatched_names?.length > 0 && (
                      <div className="text-destructive mt-1">Noms non reconnus : {gmailConfig.last_sync_result.unmatched_names.join(", ")}</div>
                    )}
                    {gmailConfig.last_sync_result.errors?.length > 0 && (
                      <div className="text-destructive mt-1">{gmailConfig.last_sync_result.errors[0]}</div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            {gmailConfig?.configured && (
              <Button variant="ghost" className="text-destructive mr-auto" onClick={removeGmail} data-testid="gmail-remove-btn">
                <Trash2 className="h-4 w-4 mr-2" /> Supprimer la config
              </Button>
            )}
            <Button variant="outline" className="rounded-full" onClick={() => setGmailOpen(false)}>Fermer</Button>
            <Button className="rounded-full bg-primary active:scale-95" onClick={saveGmail} data-testid="gmail-save-btn">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
